import { useCallback, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import { UnsavedChangesDialog } from '@/components/common/UnsavedChangesDialog';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useDiscardGuard } from '@/hooks';
import { useCreateTenant, useUpdateTenant } from '../hooks/useTenantMutations';
import { describeTenantError, getTenantErrorCode } from '../lib/tenantErrors';
import {
	slugifyTenantName,
	TENANT_SLUG_MAX_LENGTH,
	TENANT_SLUG_PATTERN,
} from '../lib/tenantSlug';
import type { Tenant } from '../types';

const NAME_MAX_LENGTH = 255;

const nameSchema = z
	.string()
	.trim()
	.min(1, 'Enter a tenant name.')
	.max(NAME_MAX_LENGTH, `Use at most ${NAME_MAX_LENGTH} characters.`);

const createSchema = z.object({
	name: nameSchema,
	slug: z
		.string()
		.trim()
		.min(3, 'Use at least 3 characters.')
		.max(
			TENANT_SLUG_MAX_LENGTH,
			`Use at most ${TENANT_SLUG_MAX_LENGTH} characters.`,
		)
		.regex(
			TENANT_SLUG_PATTERN,
			'Use lowercase letters, numbers and hyphens; no hyphen at the start or end.',
		),
});

type TenantFormValues = z.infer<typeof createSchema>;

const SLUG_TAKEN = 'TENANT_SLUG_ALREADY_EXISTS';

interface TenantFormDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The tenant to rename; omitted to create a new one. */
	tenant?: Tenant | null;
	onCreated?: (tenant: Tenant) => void;
}

/**
 * Create a tenant (name + slug) or rename one. The slug follows the name
 * until it is edited by hand, and is fixed once the tenant exists.
 */
export function TenantFormDialog({
	open,
	onOpenChange,
	tenant,
	onCreated,
}: TenantFormDialogProps) {
	const renaming = Boolean(tenant);
	const create = useCreateTenant();
	const update = useUpdateTenant();
	const mutation = renaming ? update : create;
	const slugTouched = useRef(false);

	const form = useForm<TenantFormValues>({
		resolver: zodResolver(
			// Renaming validates the name only; the slug field is not shown.
			renaming
				? createSchema.pick({ name: true }).extend({ slug: z.string() })
				: createSchema,
		),
		defaultValues: { name: '', slug: '' },
	});

	useEffect(() => {
		if (!open) return;
		form.reset({ name: tenant?.name ?? '', slug: tenant?.slug ?? '' });
		slugTouched.current = false;
		create.reset();
		update.reset();
		// Only re-seed when the dialog opens.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, tenant?.id]);

	const close = useCallback(() => onOpenChange(false), [onOpenChange]);
	const guard = useDiscardGuard(form.formState.isDirty, close);

	const handleSubmit = form.handleSubmit((values) => {
		if (mutation.isPending) return;

		if (tenant) {
			update.mutate(
				{ id: tenant.id, input: { name: values.name } },
				{
					onSuccess: (updated) => {
						toast.success(`Renamed to ${updated.name}`);
						onOpenChange(false);
					},
					onError: (error) => toast.error(describeTenantError(error)),
				},
			);
			return;
		}

		create.mutate(
			{ name: values.name, slug: values.slug },
			{
				onSuccess: (created) => {
					onOpenChange(false);
					onCreated?.(created);
				},
				onError: (error) => {
					if (getTenantErrorCode(error) === SLUG_TAKEN) {
						form.setError('slug', { message: describeTenantError(error) });
						return;
					}
					toast.error(describeTenantError(error));
				},
			},
		);
	});

	const showGeneralError =
		mutation.isError && getTenantErrorCode(mutation.error) !== SLUG_TAKEN;

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (next) onOpenChange(true);
				else guard.requestClose();
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{renaming ? 'Rename tenant' : 'Add tenant'}</DialogTitle>
					<DialogDescription>
						{renaming
							? 'The slug and the tenant ID stay the same.'
							: 'A new, empty workspace. Invite its first admin right after.'}
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={handleSubmit} className="space-y-4" noValidate>
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Name</FormLabel>
									<FormControl>
										<Input
											placeholder="Acme Media"
											autoComplete="off"
											maxLength={NAME_MAX_LENGTH}
											{...field}
											onChange={(event) => {
												field.onChange(event);
												if (!renaming && !slugTouched.current) {
													form.setValue(
														'slug',
														slugifyTenantName(event.target.value),
														{ shouldDirty: true },
													);
												}
											}}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{!renaming && (
							<FormField
								control={form.control}
								name="slug"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Slug</FormLabel>
										<FormControl>
											<Input
												placeholder="acme-media"
												autoComplete="off"
												spellCheck={false}
												maxLength={TENANT_SLUG_MAX_LENGTH}
												className="font-mono"
												{...field}
												onChange={(event) => {
													slugTouched.current = true;
													field.onChange(event.target.value.toLowerCase());
												}}
											/>
										</FormControl>
										<FormDescription>
											Short unique key; cannot be changed later.
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						{showGeneralError && (
							<p className="text-destructive text-sm" role="alert">
								{describeTenantError(mutation.error)}
							</p>
						)}

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={guard.requestClose}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={mutation.isPending}>
								{mutation.isPending && <Loader2 className="animate-spin" />}
								{renaming ? 'Save' : 'Add tenant'}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
			<UnsavedChangesDialog
				open={guard.asking}
				onStay={guard.stay}
				onDiscard={guard.discard}
			/>
		</Dialog>
	);
}
